// =====================================================================
// SpellcheckLayer (S118) — corretor estilo LanguageTool no campo de mensagem.
// - Debounce → chama /spellcheck (LanguageTool self-hosted; LGPD: fica no VPS).
// - Clicar numa palavra com erro → popup limpo de sugestões (1 clique corrige).
// - Barra "✍️ N correções" (portal fixo) → abre a lista de todos os erros.
// Robusto: acha o erro pelo selectionStart do textarea (NÃO depende de
// alinhamento visual). Fail-safe: se o backend responder enabled=false, o
// componente vira inerte (nada some, nada chama). Zero impacto de layout
// (tudo via portal com position:fixed). Sem dependência nova.
// =====================================================================
import React, { useEffect, useRef, useState, useCallback } from "react";
import ReactDOM from "react-dom";
import { checkSpelling } from "../../services/spellcheckService";

const DEBOUNCE_MS = 700;
const ACCENT = "#2f6fed"; // azul das sugestões (imagem 2)

const keyOf = (m, text) => `${m.ruleId}::${text.substr(m.offset, m.length)}`;

export default function SpellcheckLayer({ inputRef, value }) {
  const [matches, setMatches] = useState([]);
  const [wordPopup, setWordPopup] = useState(null); // { match, x, y }
  const [listOpen, setListOpen] = useState(false);
  const [rect, setRect] = useState(null); // bounding do textarea (barra/lista)

  const disabledRef = useRef(false); // company sem a flag → inerte
  const ignoreRef = useRef(new Set()); // erros ignorados nesta sessão
  const timerRef = useRef(null);
  const checkedTextRef = useRef(""); // texto pelo qual os offsets valem

  // -- posiciona a barra/lista logo acima do textarea (portal fixo) ----------
  const refreshRect = useCallback(() => {
    const el = inputRef && inputRef.current;
    if (el) setRect(el.getBoundingClientRect());
  }, [inputRef]);

  useEffect(() => {
    refreshRect();
    window.addEventListener("scroll", refreshRect, true);
    window.addEventListener("resize", refreshRect);
    return () => {
      window.removeEventListener("scroll", refreshRect, true);
      window.removeEventListener("resize", refreshRect);
    };
  }, [refreshRect, matches, listOpen]);

  // -- debounce + fetch ------------------------------------------------------
  useEffect(() => {
    if (disabledRef.current) return undefined;
    const text = String(value || "");
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!text.trim()) {
      setMatches([]);
      setWordPopup(null);
      setListOpen(false);
      return undefined;
    }
    timerRef.current = setTimeout(async () => {
      try {
        const res = await checkSpelling(text);
        if (res && res.enabled === false) {
          disabledRef.current = true;
          setMatches([]);
          return;
        }
        checkedTextRef.current = text;
        const ms = (res && res.matches ? res.matches : []).filter(
          m =>
            m.replacements &&
            m.replacements.length > 0 &&
            !ignoreRef.current.has(keyOf(m, text))
        );
        setMatches(ms);
        refreshRect();
      } catch (e) {
        /* fail-open: LT fora do ar → campo segue normal */
      }
    }, DEBOUNCE_MS);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value, refreshRect]);

  // -- clique na palavra com erro → popup ------------------------------------
  useEffect(() => {
    const el = inputRef && inputRef.current;
    if (!el) return undefined;
    const onClick = ev => {
      if (String(value || "") !== checkedTextRef.current) return; // offsets defasados
      const caret = el.selectionStart;
      const hit = matches.find(
        m => caret >= m.offset && caret <= m.offset + m.length
      );
      if (hit) setWordPopup({ match: hit, x: ev.clientX, y: ev.clientY });
      else setWordPopup(null);
    };
    el.addEventListener("click", onClick);
    return () => el.removeEventListener("click", onClick);
  }, [inputRef, matches, value]);

  // -- fecha popups ao clicar fora / Esc -------------------------------------
  useEffect(() => {
    if (!wordPopup && !listOpen) return undefined;
    const onDoc = e => {
      if (e.target.closest && e.target.closest("[data-sc]")) return;
      setWordPopup(null);
      setListOpen(false);
    };
    const onKey = e => {
      if (e.key === "Escape") {
        setWordPopup(null);
        setListOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [wordPopup, listOpen]);

  // -- aplicar / ignorar -----------------------------------------------------
  const applyFix = (m, replacement) => {
    const el = inputRef && inputRef.current;
    const text = String(value || "");
    if (text !== checkedTextRef.current) {
      setWordPopup(null);
      return;
    }
    const next =
      text.slice(0, m.offset) + replacement + text.slice(m.offset + m.length);
    const caret = m.offset + replacement.length;
    setMatches(prev => prev.filter(x => x !== m));
    setWordPopup(null);

    // Escreve no textarea via o setter nativo do React (dispara o onChange do
    // MUI/InputBase → mantém o estado inputMessage em sincronia, sem tocar no
    // componente pai).
    if (el) {
      const proto = window.HTMLTextAreaElement && window.HTMLTextAreaElement.prototype;
      const setter = proto && Object.getOwnPropertyDescriptor(proto, "value");
      if (setter && setter.set) setter.set.call(el, next);
      else el.value = next;
      el.dispatchEvent(new Event("input", { bubbles: true }));
      requestAnimationFrame(() => {
        try {
          el.focus();
          el.setSelectionRange(caret, caret);
        } catch (e) {
          /* noop */
        }
      });
    }
  };

  const ignoreFix = m => {
    const text = String(value || "");
    ignoreRef.current.add(keyOf(m, text));
    setMatches(prev => prev.filter(x => x !== m));
    setWordPopup(null);
  };

  if (disabledRef.current) return null;
  const count = matches.length;
  const wordText = m => String(value || "").substr(m.offset, m.length);

  // -- estilos (inline; sem JSS pra não brigar com o MUI) --------------------
  const card = {
    background: "#1f2023",
    color: "#f2f3f5",
    borderRadius: 10,
    boxShadow: "0 6px 24px rgba(0,0,0,.45)",
    border: "1px solid rgba(255,255,255,.08)",
    padding: "10px 12px",
    zIndex: 13000,
    maxWidth: 320,
    fontSize: 13,
    lineHeight: 1.35
  };
  const pill = {
    background: ACCENT,
    color: "#fff",
    border: "none",
    borderRadius: 7,
    padding: "5px 11px",
    margin: "4px 6px 0 0",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 600
  };
  const ignoreBtn = {
    background: "transparent",
    color: "#aab0bd",
    border: "1px solid rgba(255,255,255,.18)",
    borderRadius: 7,
    padding: "5px 11px",
    margin: "4px 0 0 0",
    cursor: "pointer",
    fontSize: 13
  };

  // clamp horizontal do popup na viewport
  const clampX = x => Math.min(Math.max(8, x), window.innerWidth - 330);

  // -- popup de UMA palavra (clique) -----------------------------------------
  const renderWordPopup = () => {
    if (!wordPopup) return null;
    const m = wordPopup.match;
    const top = Math.max(8, wordPopup.y - 12 - 120);
    return ReactDOM.createPortal(
      <div
        data-sc
        style={{
          position: "fixed",
          left: clampX(wordPopup.x - 20),
          top,
          ...card
        }}
      >
        <div style={{ display: "flex", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontWeight: 700 }}>Ortografia</span>
          <span
            style={{
              marginLeft: 8,
              color: "#aab0bd",
              textDecoration: "line-through"
            }}
          >
            {wordText(m)}
          </span>
        </div>
        {m.message ? (
          <div style={{ color: "#c7ccd6", marginBottom: 2 }}>{m.message}</div>
        ) : null}
        <div style={{ display: "flex", flexWrap: "wrap" }}>
          {m.replacements.map((r, i) => (
            <button
              key={i}
              data-sc
              style={pill}
              onClick={() => applyFix(m, r)}
            >
              {r}
            </button>
          ))}
          <button data-sc style={ignoreBtn} onClick={() => ignoreFix(m)}>
            Ignorar
          </button>
        </div>
      </div>,
      document.body
    );
  };

  // -- barra "✍️ N correções" + lista ----------------------------------------
  const renderBar = () => {
    if (!rect || count === 0) return null;
    const barTop = Math.max(6, rect.top - 34);
    const barLeft = Math.max(8, rect.left);
    return ReactDOM.createPortal(
      <div data-sc style={{ position: "fixed", left: barLeft, top: barTop, zIndex: 12900 }}>
        <button
          data-sc
          onClick={() => setListOpen(o => !o)}
          style={{
            background: "#1f2023",
            color: "#f2f3f5",
            border: "1px solid rgba(255,255,255,.12)",
            borderRadius: 16,
            padding: "4px 12px",
            cursor: "pointer",
            fontSize: 12.5,
            fontWeight: 600,
            boxShadow: "0 3px 12px rgba(0,0,0,.35)"
          }}
        >
          ✍️ {count} {count === 1 ? "correção" : "correções"}
        </button>

        {listOpen ? (
          <div
            data-sc
            style={{
              position: "absolute",
              bottom: 38,
              left: 0,
              ...card,
              maxHeight: 260,
              overflowY: "auto",
              width: 300
            }}
          >
            {matches.map((m, idx) => (
              <div
                key={idx}
                style={{
                  padding: "6px 0",
                  borderBottom:
                    idx < matches.length - 1
                      ? "1px solid rgba(255,255,255,.07)"
                      : "none"
                }}
              >
                <div style={{ marginBottom: 2 }}>
                  <span
                    style={{ color: "#e0666f", textDecoration: "line-through", fontWeight: 600 }}
                  >
                    {wordText(m)}
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  {m.replacements.slice(0, 5).map((r, i) => (
                    <button key={i} data-sc style={pill} onClick={() => applyFix(m, r)}>
                      {r}
                    </button>
                  ))}
                  <button data-sc style={ignoreBtn} onClick={() => ignoreFix(m)}>
                    Ignorar
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : null}
      </div>,
      document.body
    );
  };

  return (
    <>
      {renderBar()}
      {renderWordPopup()}
    </>
  );
}
